import { AssistantMode, ToolCallStatus } from "@/models/conversation";
import { Content } from "@/models/document";
import { BackendWidget } from "@/models/widget-v2";

// ============================================================================
// Tool Call Entry (with Date timestamps for UI)
// ============================================================================

export interface ToolCallEntry {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  status: ToolCallStatus;
  result?: unknown;
  timestamp: Date;
}

// ============================================================================
// User Question (for ask_user tool)
// ============================================================================

export interface UserQuestionOption {
  id: string;
  label: string;
}

export interface UserQuestionItem {
  question: string;
  options: UserQuestionOption[];
  allowMultiple?: boolean;
}

export interface UserQuestion {
  questions: UserQuestionItem[];
  title?: string;
}

// ============================================================================
// Action Confirmation (for mutating actions in ask/plan mode)
// ============================================================================

export interface PendingAction {
  actionId: string;
  toolName: string;
  method: string;
  path: string;
  params?: Record<string, unknown>;
  body?: Record<string, unknown>;
  status: "pending" | "executing" | "completed" | "rejected" | "error";
  result?: unknown;
  error?: string;
}

// ============================================================================
// Chat Message (with Date timestamps for UI)
// ============================================================================

export interface ChatMessage {
  id: string;
  text: string;
  sender: "user" | "assistant";
  timestamp: Date;
  widget?: BackendWidget;
  documents?: Content[];
  toolCalls?: ToolCallEntry[];
  /** User question from ask_user tool (assistant asking user for input) */
  userQuestion?: UserQuestion;
  /** User's response to a question (selected option IDs) */
  userQuestionResponse?: string[];
  /** Pending action requiring user confirmation (mutating actions in ask/plan mode) */
  pendingAction?: PendingAction;
}

// ============================================================================
// Quick Action
// ============================================================================

export interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  prompt: string;
}

// ============================================================================
// Mode Config
// ============================================================================

export interface ModeConfig {
  id: AssistantMode;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  bgColor: string;
  textColor: string;
}

// ============================================================================
// Theme Config
// ============================================================================

export interface ChatTheme {
  bg: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  inputBg: string;
  inputBorder: string;
  buttonBg: string;
  buttonHover: string;
  userMsgBg: string;
  userMsgText: string;
  assistantMsgBg: string;
  assistantMsgText: string;
  shadow: string;
  isDark: boolean;
}

// ============================================================================
// Widget Detection Helpers
// ============================================================================

/**
 * Check if a tool call is a widget creation request (POST /widgets).
 * Handles paths with or without leading slash.
 */
export function isWidgetCreationCall(
  toolName: string,
  args?: Record<string, unknown>,
): boolean {
  if (toolName !== "call_api") return false;
  if (!args) return false;

  const method = (args.method as string)?.toUpperCase();
  const path = (args.path as string)?.replace(/^\//, "");

  return method === "POST" && path?.replace(/\/$/, "") === "widgets";
}

/**
 * Check if a tool call fetches an existing widget (GET /widgets/:id).
 * Used during live streaming to show a preview, but NOT on reload
 * since the widget message is already persisted.
 */
export function isWidgetRetrievalCall(
  toolName: string,
  args?: Record<string, unknown>,
): boolean {
  if (toolName !== "call_api") return false;
  if (!args) return false;

  const method = (args.method as string)?.toUpperCase();
  const path = (args.path as string)?.replace(/^\//, "");

  return method === "GET" && !!path?.startsWith("widgets/");
}

/**
 * Check if the result from a tool call is a valid widget object.
 * Widget objects have doc_type or chart field indicating widget type,
 * or have widget-specific fields like bucket_field, metrics, etc.
 */
export function isWidgetResult(
  result: unknown,
): result is Record<string, unknown> {
  if (!result || typeof result !== "object" || Array.isArray(result))
    return false;

  const obj = result as Record<string, unknown>;

  // Must have an ID
  const hasId = typeof obj.id === "string";
  if (!hasId) return false;

  // Check for widget-specific type fields
  const hasDocType =
    typeof obj.doc_type === "string" && obj.doc_type.includes("widget");
  const hasChart =
    typeof obj.chart === "string" && obj.chart.includes("widget");
  const hasType = typeof obj.type === "string" && obj.type.includes("widget");

  // Check for widget-specific data fields (as fallback)
  const hasWidgetFields =
    obj.bucket_field !== undefined ||
    obj.metrics !== undefined ||
    obj.field !== undefined ||
    obj.metric !== undefined ||
    obj.size !== undefined ||
    obj.sort !== undefined;

  return hasDocType || hasChart || hasType || hasWidgetFields;
}

/**
 * Extract widget data from various wrapper formats.
 * Handles nested responses like { result: widget }, { data: widget }, etc.
 * Also handles arrays that might contain a single widget.
 */
export function extractWidgetData(data: unknown): unknown {
  if (!data) {
    return data;
  }

  // If it's an array with a single object, extract that object
  if (Array.isArray(data)) {
    if (data.length === 1 && typeof data[0] === "object") {
      return extractWidgetData(data[0]);
    }
    return data;
  }

  if (typeof data !== "object") {
    return data;
  }

  const objData = data as Record<string, unknown>;

  // Check if it's wrapped in a result property
  if ("result" in objData && objData.result) {
    return extractWidgetData(objData.result);
  }

  // Check if it's wrapped in a data property
  if ("data" in objData && objData.data) {
    return extractWidgetData(objData.data);
  }

  // Check if it's a single-key object wrapper
  const keys = Object.keys(objData);
  if (keys.length === 1 && typeof objData[keys[0]] === "object") {
    const potentialWidget = objData[keys[0]];
    if (isWidgetResult(potentialWidget)) {
      return potentialWidget;
    }
  }

  return data;
}

/**
 * Convert a widget result from call_api to a BackendWidget.
 */
export function parseWidgetFromResult(
  result: Record<string, unknown>,
): BackendWidget {
  const widgetType =
    (result.doc_type as string) || (result.chart as string) || "line_widget";

  return {
    id: result.id as string,
    doc_type: widgetType,
    chart: widgetType as BackendWidget["chart"],
    name: (result.name as string) || undefined,
    metadata: result.metadata as { name: string } | undefined,
    // Copy all other fields from the result
    ...result,
  } as BackendWidget;
}
